import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = "https://ywoxtcjwhzooplrvciqj.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl3b3h0Y2p3aHpvb3BscnZjaXFqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc4OTcxMzQsImV4cCI6MjA3MzQ3MzEzNH0.i49yJ0hC1l3tdIWshzM7KF6jDs4veRVlM7SYea80EdI";

export async function callEdgeFunction(functionName: string, body: any = {}, options?: { timeoutMs?: number }) {
  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  
  // 🔒 Get user's JWT token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || SUPABASE_ANON_KEY;
  
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify(body),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Function ${functionName} failed: ${errorText}`);
    }

    return response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const timeoutError = new Error(`TIMEOUT: ${functionName} excedeu ${Math.round(timeoutMs / 1000)}s — o processo pode ter completado no servidor`);
      (timeoutError as any).isTimeout = true;
      throw timeoutError;
    }
    throw error;
  }
}

export async function syncStandings(leagueApiIds?: number[], season?: number) {
  const body: any = { leagues: leagueApiIds };
  if (season !== undefined) body.season = season;
  return callEdgeFunction('fetch-standings', body);
}

export async function syncFixtures(leagueApiIds?: number[], date?: string, season?: number) {
  const body: any = { date, leagues: leagueApiIds };
  if (season !== undefined) body.season = season;
  return callEdgeFunction('fetch-fixtures', body);
}

export async function syncFutureFixtures(leagueApiIds?: number[], days: number = 7, season?: number) {
  const body: any = { days, leagues: leagueApiIds };
  if (season !== undefined) body.season = season;
  return callEdgeFunction('fetch-fixtures', body);
}

export async function syncFixturesRange(from: string, to: string, leagueApiIds?: number[], season?: number) {
  const body: any = { from, to, leagues: leagueApiIds };
  if (season !== undefined) body.season = season;
  return callEdgeFunction('fetch-fixtures', body);
}

export async function syncOdds(fixtureIds: number[]) {
  return callEdgeFunction('fetch-odds', {
    fixture_ids: fixtureIds
  });
}

export async function fetchGameStatistics(fixtureId: number) {
  return callEdgeFunction('fetch-game-statistics', {
    fixture_id: fixtureId
  });
}

export async function fetchGameEvents(fixtureId: number) {
  return callEdgeFunction('fetch-game-events', {
    fixture_id: fixtureId
  });
}

export async function fetchHeadToHead(teamHomeApiId: number, teamAwayApiId: number, limit: number = 10) {
  return callEdgeFunction('fetch-h2h', {
    team_home_api_id: teamHomeApiId,
    team_away_api_id: teamAwayApiId,
    limit
  });
}

export async function cleanDuplicateEvents() {
  return callEdgeFunction('clean-duplicate-events');
}

export async function validateGameIntegrity(fixtureId?: number, daysBack: number = 7, leagueApiId?: number) {
  return callEdgeFunction('validate-game-integrity', {
    fixture_id: fixtureId,
    days_back: daysBack,
    league_api_id: leagueApiId
  });
}

export async function resyncFixtureScore(fixtureId: number) {
  return callEdgeFunction('fetch-fixtures', {
    batch_ids: [fixtureId]
  });
}

export async function fetchGameLineups(fixtureId: number) {
  return callEdgeFunction('fetch-lineups', {
    fixture_id: fixtureId
  });
}

export async function autoResyncByLeague(
  leagueApiId?: number, 
  daysBack: number = 6,
  season: number = 2025
) {
  return callEdgeFunction('auto-resync-by-league', {
    league_api_id: leagueApiId,
    days_back: daysBack,
    season
  });
}

export async function triggerScheduledAutoResync(mode: 'national' | 'european' = 'national') {
  return callEdgeFunction('scheduled-auto-resync', { mode });
}

export async function fixMassiveDuplicates(fixtureId: number) {
  return callEdgeFunction('fix-massive-duplicates', {
    fixture_id: fixtureId
  });
}

export async function fixSubstitutionDuplicates() {
  return callEdgeFunction('fix-substitution-duplicates');
}

export async function fixMultipleGames(fixtureIds: number[]) {
  const results = [];
  
  for (const fixtureId of fixtureIds) {
    try {
      const result = await fixMassiveDuplicates(fixtureId);
      results.push({ fixtureId, success: true, result });
    } catch (error: any) {
      results.push({ fixtureId, success: false, error: error.message });
    }
  }
  
  return results;
}

export async function processStandingsQueue() {
  return callEdgeFunction('standings-queue-processor');
}

export async function fetchPredictions(fixtureId: number) {
  return callEdgeFunction('fetch-predictions', {
    fixture_id: fixtureId
  });
}

export async function queueAllStandings() {
  return callEdgeFunction('queue-all-standings');
}

export async function checkCronHealth() {
  return callEdgeFunction('check-cron-health');
}

export async function syncTeamPlayers(teamId: number, season: number = 2025) {
  return callEdgeFunction('sync-team-players', {
    team_id: teamId,
    season
  });
}

export async function cleanDuplicateEventsV2(fixtureId?: number, dryRun: boolean = true) {
  return callEdgeFunction('clean-duplicate-events-v2', {
    fixture_id: fixtureId,
    dry_run: dryRun
  });
}

export async function validateEventsSanity(fixtureId: number) {
  return callEdgeFunction('validate-events-sanity', {
    fixture_id: fixtureId
  });
}

export async function fetchTopScorers(leagueApiId: number, season: number = 2025) {
  return callEdgeFunction('fetch-top-scorers', {
    league_api_id: leagueApiId,
    season
  });
}

export async function scheduledTopScorersSync(season: number = 2025) {
  return callEdgeFunction('scheduled-top-scorers-sync', {
    season
  });
}

export async function intelligentTopScorersSync(season: number = 2025) {
  return callEdgeFunction('intelligent-top-scorers-sync', {
    season
  });
}

export async function processTopScorersQueue(season: number = 2025) {
  return callEdgeFunction('process-top-scorers-queue', {
    season
  });
}

export async function fixPlayerNationalities() {
  return callEdgeFunction('fix-player-nationalities');
}

export async function prefetchPredictions() {
  return callEdgeFunction('prefetch-predictions');
}


export async function batchTriggerAnalyses(options: {
  league_api_id: number;
  date: string;
  batch_size?: number;
  force?: boolean;
  fixture_ids?: number[];
  skip_cooldown?: boolean;
}) {
  return callEdgeFunction('batch-trigger-analyses', options);
}

export async function batchEnrichLeague(options: {
  league_api_id: number;
  enrich_events?: boolean;
  enrich_statistics?: boolean;
  enrich_lineups?: boolean;
  enrich_sportmonks?: boolean;
  batch_size?: number;
  delay_ms?: number;
  dry_run?: boolean;
}) {
  return callEdgeFunction('batch-enrich-league', options, { timeoutMs: 120000 });
}

export async function syncFixturesForLeague(
  leagueApiId: number, 
  from: string, 
  to: string, 
  season?: number
) {
  const body: any = { leagues: [leagueApiId], from, to };
  if (season !== undefined) body.season = season;
  return callEdgeFunction('fetch-fixtures', body);
}

export async function resetEmptyAnalyses(fixtureIds?: number[], resetAll: boolean = false, force: boolean = true) {
  return callEdgeFunction('reset-empty-analyses', {
    fixture_ids: fixtureIds,
    reset_all: resetAll,
    force
  });
}

export async function fetchFixturePlayers(fixtureId: number) {
  return callEdgeFunction('fetch-fixture-players', {
    fixture_id: fixtureId
  });
}

export async function backfillPlayerSnapshots(options: {
  fixture_ids?: number[];
  days?: number;
  league_ids?: number[];
  limit?: number;
  dry_run?: boolean;
}) {
  return callEdgeFunction('backfill-player-snapshots', options);
}

// ─── GET helper for query-param-based edge functions ────────────────────────

export async function callEdgeFunctionGET(functionName: string, params: Record<string, string | number>) {
  const SUPABASE_URL = "https://ywoxtcjwhzooplrvciqj.supabase.co";
  const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl3b3h0Y2p3aHpvb3BscnZjaXFqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc4OTcxMzQsImV4cCI6MjA3MzQ3MzEzNH0.i49yJ0hC1l3tdIWshzM7KF6jDs4veRVlM7SYea80EdI";

  const queryString = new URLSearchParams(
    Object.entries(params).reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {} as Record<string, string>)
  ).toString();

  const url = `${SUPABASE_URL}/functions/v1/${functionName}?${queryString}`;

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || SUPABASE_ANON_KEY;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Function ${functionName} GET failed: ${errorText}`);
  }

  return response.json();
}

// ─── Sportmonks Fixture Mapping ─────────────────────────────────────────────

/** GET (public): Read from fixture_mappings cache */
export async function resolveSportmonksFixture(fixtureId: number) {
  return callEdgeFunctionGET('resolve-sportmonks-fixture', { fixture_id: fixtureId });
}

/** POST (authenticated): Resolve mapping + optionally fetch ballCoordinates */
export async function resolveSportmonksFixtureAuth(
  fixtureId: number,
  options?: { include_ball_coordinates?: boolean }
) {
  return callEdgeFunction('resolve-sportmonks-fixture', {
    fixture_id: fixtureId,
    ...options,
  });
}

export async function batchWeatherSync() {
  return callEdgeFunction('batch-weather-sync');
}

/** GET (public): Fetch ball coordinates without auth for already-resolved fixtures */
export async function fetchBallCoordinatesPublic(fixtureId: number) {
  return callEdgeFunctionGET('resolve-sportmonks-fixture', {
    fixture_id: fixtureId,
    ball: 1,
  });
}

// ─── Analysis Translation ───────────────────────────────────────────────────

export async function translateAnalysis(fixtureId: number, lang: string) {
  return callEdgeFunction('translate-analysis', {
    fixture_id: fixtureId,
    lang
  });
}

export async function backfillBallCoordinates(date?: string, maxPerRun?: number) {
  return callEdgeFunction('backfill-ball-coordinates', {
    date,
    max_per_run: maxPerRun
  });
}

export async function fetchSportmonksStatistics(fixtureId: number) {
  return callEdgeFunction('fetch-sportmonks-statistics', {
    fixture_id: fixtureId
  });
}

export async function fetchRefereesSportmonks(fixtureIds?: number[], daysAhead?: number) {
  return callEdgeFunction('fetch-referees-sportmonks', {
    ...(fixtureIds ? { fixture_ids: fixtureIds } : {}),
    ...(daysAhead !== undefined ? { days_ahead: daysAhead } : {}),
  });
}

export async function computeTeamMetrics(season: string = '2025', leagueApiId?: number) {
  return callEdgeFunction('compute-team-metrics', {
    season,
    ...(leagueApiId ? { league_api_id: leagueApiId } : {}),
  });
}
